// „Co powiesz?” (kurs v2, krok 6; SPEC §23.13.3, §23.36): kwestia rozmówcy po angielsku w dymku (z dosłownym tłumaczeniem pod spodem), pod nim brzoskwiniowe pole z poleceniem po polsku
// („Zapytaj grzecznie, gdzie jest Ben.” i podtytuł „Wybierz najlepsze pytanie po angielsku.”) i karty ze zdaniami (jedno pasuje, reszta to słowa kogoś innego).
// Dziecko MÓWI zdanie, które pasuje. Mikrofon słucha poprawnej odpowiedzi (Azure ocenia wymowę wobec niej), a CO zostało powiedziane, rozstrzyga `judgeSpoken`
// na tekście rozpoznanym: poprawne zdanie (także jego wariant), zdanie z innej karty („To mówi sprzedawca”) albo nic sensownego.
// Poziomy podpowiedzi: 1 = karty ze zdaniami, 🔊 i 🐢, 2 = same 🔊 i 🐢 (tekst po „Podpowiedź”), 3 = samo polecenie. Dwie nieudane próby obniżają poziom o jeden.
// Bez mikrofonu (brak oceny, odmowa, błąd sieci, trzy razy cisza) dziecko dotyka karty ze zdaniem, które pasuje — ten sam ekran, bez ocen wymowy.
import { config } from '../config.ts';
import { prettyHeard } from '../lib/display.ts';
import { feedbackText, heardLine, sayKey } from '../lib/feedbackVoice.ts';
import type { FeedbackKey, FeedbackLang } from '../lib/feedbackVoice.ts';
import { decide } from '../lib/scoring.ts';
import { judgeSpoken } from '../lib/say.ts';
import { runAssessment } from '../speech/assessors.ts';
import { AssessError } from '../speech/types.ts';
import type { AssessmentResult, SayCard, SayTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h, shake, trackPlSlot } from './dom.ts';
import { tr, tx, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { dialogueLine } from './dialogue.ts';
import { fitToWindow, icon, sayFitProps, starsEl, translateButton } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  chip: { en: 'What do you say?', pl: 'Co powiesz?' },
  speak: { en: 'Speak', pl: 'Mów' },
  skip: { en: 'Skip', pl: 'Pomiń' },
  tapHint: { en: 'Tap the sentence you would say.', pl: 'Dotknij zdania, które byś powiedział.' },
  pick: { en: 'This one', pl: 'To' },
  otherCard: { en: 'That sentence is for someone else. What do you say?', pl: 'To zdanie jest dla kogoś innego. A co powiesz ty?' },
  playCard: { en: 'Play this sentence', pl: 'Odtwórz to zdanie' },
  slowCard: { en: 'Play this sentence slowly', pl: 'Odtwórz to zdanie wolno' },
} as const satisfies Record<string, Pair>;

/**
 * Podtytuł brzoskwiniowego pola z poleceniem (makieta „Co powiesz?”, SPEC §23.36): zawsze po polsku, bo to polecenie dla dziecka, a nie napis interfejsu. Przy dotykaniu kart — jakiego zdania szukać
 * (pytania, gdy poprawna odpowiedź jest pytaniem, inaczej „zdania”), przy mówieniu — „Powiedz po angielsku.”.
 */
export function sayAskText(tapMode: boolean, answer: string): string {
  if (!tapMode) return 'Powiedz po angielsku.';
  return answer.trim().endsWith('?') ? 'Wybierz najlepsze pytanie po angielsku.' : 'Wybierz najlepsze zdanie po angielsku.';
}

/** Po tylu nieudanych próbach mówienia pokazujemy poprawną odpowiedź i idziemy dalej. */
export const SAY_MAX_TRIES = 4;
/** Co tyle nieudanych prób poziom podpowiedzi rośnie o jeden (3 → 2 → 1). */
export const SAY_RAISE_EVERY = 2;

type Level = 1 | 2 | 3;

export function renderSay(task: SayTask, env: TaskEnv): TaskView {
  const { ctx } = env;
  const { player, teacher } = ctx;
  const comp = env.companion;
  const langNow = (): FeedbackLang => teacher?.lang() ?? config.teacher.defaultLang;
  const correctCard = task.cards.find((c) => c.correct);
  const distractors = task.cards.filter((c) => !c.correct);

  let level: Level = task.level;
  let attempts = 0; // próby mówienia z rozpoznanym tekstem albo dotknięcia karty
  let failed = 0;
  let silentTries = 0;
  let hinted = false;
  let busy = false;
  let micReady = false;
  let refit: () => void = () => {}; // ustawiane po zbudowaniu ekranu (fitToWindow); wołane, gdy układ zmienia się sam (odsłonięte karty, tryb dotykowy)
  let tapMode = ctx.chain().assessors.length === 0; // bez jakiejkolwiek oceny od razu dotykamy kart
  let finished = false;
  let solved = false;
  let stars = 0;
  const wrongCards = new Set<SayCard>();
  let fb: (() => HTMLElement[]) | null = null; // ostatni komunikat (przerysowujemy go po zmianie języka)
  if (tapMode && level === 3) level = 1;

  const feedback = h('div', { class: 'result say-result', 'data-testid': 'feedback', 'aria-live': 'polite' });
  const actions = h('div', { class: 'actions-bar' });
  const skipRow = h('div', { class: 'actions skip-row' });
  const cardsBox = h('div', { class: 'say-cards', 'data-testid': 'say-cards' });
  const pls: HTMLElement[] = [];
  let showPl = false;
  let paintPartnerPl: (() => void) | undefined; // tłumaczenie kwestii rozmówcy (pod tekstem dymka; pokazuje je 文A): ustawiane po zbudowaniu ekranu
  let setPartnerBusy: ((busy: boolean) => void) | undefined; // 🔊 / 🐢 rozmówcy: w czasie nagrywania wyłączone, jak 🔊 przy kartach

  const speak = (parts: ReadonlyArray<{ id: string; fallback: { text: string; lang: FeedbackLang | 'en' } }>): void => {
    if (parts.length === 0) return;
    comp?.quiet();
    void player.playAll(parts, config.teacher.gapMs);
  };
  const note = (key: FeedbackKey, extra: () => HTMLElement[] = () => []): void => {
    fb = () => [h('p', { class: 'result-msg reason', 'data-testid': 'note' }, feedbackText(key, langNow())), ...extra()];
    feedback.replaceChildren(...fb());
    speak([sayKey(key, langNow())]);
  };

  const playCard = (c: SayCard): Promise<void> => {
    comp?.quiet();
    return player.play(c.audioId, { text: c.text, lang: 'en' });
  };
  const playAllCards = (): void => {
    comp?.quiet();
    void player.playAll(task.cards.map((c) => ({ id: c.audioId, fallback: { text: c.text, lang: 'en' as const } })), config.teacher.gapMs);
  };

  const playSlowCard = (c: SayCard): Promise<void> => {
    comp?.quiet();
    return player.play(c.slowAudioId ?? c.audioId, { text: c.text, lang: 'en', slow: true });
  };

  const playBtns: HTMLButtonElement[] = []; // 🔊 i 🐢 przy kartach: w czasie nagrywania wyłączone (głos z głośnika wpadłby do mikrofonu i do oceny)
  function renderCards(): void {
    paintPartnerPl?.();
    pls.length = 0;
    playBtns.length = 0;
    cardsBox.dataset.level = String(level);
    // poziom 3 (samo polecenie): karty są zbudowane, ale ukryte (`hidden`). UWAGA: reguła `.say-cards[hidden] { visibility: hidden }` miała je zostawić na miejscu, ale `[hidden] { display: none !important }` ją przebija, więc karty znikają z układu
    // i „Podpowiedź” zmienia wysokość ekranu (usterka z patcha 53, znaleziona w przeglądzie patcha 54 — SPEC §23.35.7; poziomów 2–3 nie ma w teście przeglądarkowym, więc nie poprawiam jej bez możliwości sprawdzenia).
    cardsBox.replaceChildren(...(task.cards.map((c, i) => {
      const showText = level === 1 || finished;
      const state = finished && c.correct ? ' right' : wrongCards.has(c) ? ' wrong' : finished ? ' muted' : '';
      const pl = h('p', { class: 'say-pl pl', 'data-testid': 'say-card-pl', hidden: !(showPl && showText) }, c.pl);
      pls.push(pl);
      const play = txAttr(ctx, h('button', { type: 'button', class: 'dlg-btn say-play', 'data-testid': 'say-card-play', disabled: busy, onclick: () => void playCard(c) }, icon('speaker')), 'aria-label', T.playCard);
      const slow = txAttr(ctx, h('button', { type: 'button', class: 'dlg-btn dlg-slow say-play say-slow', 'data-testid': 'say-card-slow', disabled: busy, onclick: () => void playSlowCard(c) }, icon('turtle')), 'aria-label', T.slowCard);
      playBtns.push(play, slow);
      const body = showText
        ? h('p', { class: 'say-text', 'data-testid': 'say-card-text' }, c.text)
        : h('p', { class: 'say-text hidden-text', 'data-testid': 'say-card-text', 'aria-hidden': 'true' }, '• • •');
      // kółko po lewej (jak na makiecie): w trybie dotykowym to przycisk wyboru (rozciągnięty na cały wiersz), po odpowiedzi ✓ / ✗; przy mówieniu wiersze nie mają kółka
      const mark = finished && c.correct ? icon('check') : wrongCards.has(c) ? icon('close') : null;
      const pick = tapMode && !finished && !wrongCards.has(c)
        ? txAttr(ctx, h('button', { type: 'button', class: 'say-dot say-pick', 'data-testid': 'say-pick', onclick: () => choose(c) }), 'aria-label', T.pick)
        : mark ? h('span', { class: `say-dot mark${tapMode ? '' : ' float'}`, 'aria-hidden': 'true' }, mark) : tapMode ? h('span', { class: 'say-dot ghost', 'aria-hidden': 'true' }) : null; // w trybie dotykowym karta bez znaku zostaje z pustym kółkiem, więc tekst nie przesuwa się po wyborze
      // przy mówieniu karta nie ma kółka, więc ✓ / ✗ wisi na jej lewej krawędzi (nie zabiera miejsca tekstowi — nic się nie zawija i nie przesuwa); w trybie dotykowym zajmuje miejsce kółka
      const card = h('div', { class: `say-card${state}${pick && tapMode ? ' has-dot' : ''}`, 'data-testid': 'say-card', 'data-i': i, 'data-who': c.who, 'data-correct': c.correct ? '1' : '0' },
        pick,
        h('div', { class: 'say-body' }, body, pl),
        h('div', { class: 'say-sound' }, play, slow), // 🔊 i 🐢 (makieta: obok siebie, po prawej)
      );
      trackPlSlot(card, pl);
      return card;
    })));
    cardsBox.hidden = level === 3 && !finished;
  }

  function renderControls(): void {
    for (const b of playBtns) b.disabled = busy;
    setPartnerBusy?.(busy);
    actions.replaceChildren();
    skipRow.replaceChildren();
    paintAsk?.();
    const next = (disabled: boolean) => button([tx(ctx, L.next), icon('arrow')], () => env.done({ taskId: task.id, kind: 'say', ref: task.word, correct: solved && failed === 0 && !hinted, attempts: Math.max(1, attempts), passed: solved, ...(stars > 0 ? { stars } : {}) }), { class: 'btn primary', testid: 'next', disabled });
    // odsłuch zdań jest przy każdym wierszu (🔊), więc na dole zostają tylko: „Podpowiedź” (poziomy 2–3), „Mów” (mikrofon) i „Dalej” (po rozwiązaniu; przy dotykaniu kart do tego czasu wyszarzone, żeby pasek się nie zmieniał)
    if (finished) {
      actions.className = 'actions-bar single end';
      actions.append(next(false));
      return;
    }
    const bar: HTMLElement[] = [];
    if (level > 1) bar.push(button(tx(ctx, L.hint), () => raise(), { class: 'btn ghost', testid: 'hint', disabled: busy }));
    if (!tapMode) {
      bar.push(button(tx(ctx, T.speak), () => void listen(), { class: busy ? (micReady ? 'btn mic listening' : 'btn mic waiting') : 'btn mic', testid: 'mic', disabled: busy }));
    } else if (level === 1) bar.push(next(true));
    actions.className = bar.length > 1 ? 'actions-bar two' : 'actions-bar single end';
    actions.append(...bar);
    if (failed > 0 || tapMode) skipRow.append(button(tx(ctx, T.skip), () => finish(false), { class: 'btn link', testid: 'skip' }));
  }

  /** Podpowiedź: o jeden poziom łatwiej (od dziecka albo po nieudanych próbach). */
  function raise(): void {
    if (level === 1) return;
    level = (level - 1) as Level;
    hinted = true;
    renderCards();
    renderControls();
    refit(); // podpowiedź odsłania teksty kart (poziom 3 → 2 → 1): ekran robi się wyższy
  }

  function finish(ok: boolean): void {
    finished = true;
    solved = ok;
    busy = false;
    level = 1;
    if (!ok) {
      fb = null;
      feedback.replaceChildren();
    }
    renderCards();
    renderControls();
    root.dataset.state = ok ? 'right' : 'revealed';
    refit(); // odpowiedź odsłania teksty kart (na poziomie 3 były schowane): skala dopasowuje się do nowej wysokości
    if (!ok && correctCard) void playCard(correctCard);
  }

  /** Tryb dotykowy: dziecko wybiera kartę. */
  function choose(card: SayCard): void {
    if (finished || busy) return;
    attempts++;
    if (card.correct) {
      stars = 0;
      fb = null;
      feedback.replaceChildren();
      finish(true);
      speak([sayKey('good', langNow())]);
      return;
    }
    failed++;
    wrongCards.add(card);
    renderCards();
    const why = card.whyPl;
    fb = () => [h('p', { class: 'result-msg reason', 'data-testid': 'why' }, why ?? tr(ctx, T.otherCard))];
    feedback.replaceChildren(...fb());
    const b = cardsBox.querySelector<HTMLElement>(`[data-i="${task.cards.indexOf(card)}"]`);
    if (b) shake(b);
    speak([sayKey('tryAgain', langNow())]);
    renderControls();
    refit(); // powód pod kartami może być dłuższy niż zarezerwowane pole
  }

  const onResult = (result: AssessmentResult): void => {
    attempts++;
    const heard = result.recognizedText ?? '';
    const verdict = judgeSpoken(heard, task.accept, distractors.map((c) => c.text));
    if (verdict.kind === 'correct') {
      const clean = verdict.errors === 0 && verdict.matched === task.accept[0];
      stars = clean && result.source === 'azure' ? Math.max(1, decide(result, config.scoring).stars) : 2;
      fb = () => [starsEl(stars, 3, 'stars', 'stars', ctx)];
      feedback.replaceChildren(...fb());
      finish(true);
      speak([sayKey(stars >= 3 ? 'petarda' : 'ok', langNow())]);
      return;
    }
    failed++;
    silentTries = 0;
    if (verdict.kind === 'distractor') {
      const card = distractors[verdict.index];
      if (card) wrongCards.add(card);
      const why = card?.whyPl;
      fb = () => [h('p', { class: 'result-msg reason', 'data-testid': 'why' }, why ?? tr(ctx, T.otherCard)), ...(heard ? [h('p', { class: 'heard', 'data-testid': 'heard' }, heardLine('heard', prettyHeard(heard), langNow()))] : [])];
      feedback.replaceChildren(...fb());
      speak([sayKey('tryAgain', langNow())]);
    } else {
      fb = () => [h('p', { class: 'result-msg reason', 'data-testid': 'reason' }, feedbackText('tryAgain', langNow())), ...(heard ? [h('p', { class: 'heard', 'data-testid': 'heard' }, heardLine('heard', prettyHeard(heard), langNow()))] : [])];
      feedback.replaceChildren(...fb());
      speak([sayKey('tryAgain', langNow())]);
    }
    if (failed >= SAY_MAX_TRIES) return finish(false);
    if (failed % SAY_RAISE_EVERY === 0 && level > 1) return raise();
    renderCards();
    renderControls();
    refit(); // powód i „Usłyszałem…” mogą być dłuższe niż zarezerwowane pole
  };

  async function listen(): Promise<void> {
    if (busy || finished || !correctCard) return;
    busy = true;
    micReady = false;
    comp?.quiet();
    comp?.taskBusy(true);
    player.stop();
    feedback.replaceChildren();
    fb = null;
    renderControls();
    try {
      const result = await runAssessment(ctx.chain(), task.accept[0] ?? correctCard.text, {
        onReady: () => {
          if (!busy || micReady) return;
          micReady = true;
          renderControls();
        },
      });
      busy = false;
      comp?.taskBusy(false);
      onResult(result);
    } catch (e) {
      busy = false;
      comp?.taskBusy(false);
      const code = e instanceof AssessError ? e.code : 'unavailable';
      if (code === 'no-speech') {
        silentTries++;
        if (silentTries >= 3) tapMode = true;
        note(silentTries >= 3 ? 'silentMore' : 'silent');
      } else {
        tapMode = true; // mikrofon albo ocena nie działają: dotykamy kart
        console.warn('[naative] „Co powiesz?”: ocena niedostępna:', e);
        note(code === 'mic-denied' || code === 'no-mic' ? 'micOff' : code === 'unavailable' && e instanceof AssessError && e.causes.includes('network') ? 'network' : 'noCheck');
      }
      if (tapMode && level === 3) level = 1;
      renderCards();
      renderControls();
      refit(); // przejście na dotykanie kart (karty poziomu 3 były schowane) zmienia wysokość ekranu — skala dopasowuje się od nowa
    }
  }

  const plToggle = translateButton(false, (on) => {
    showPl = on;
    renderCards();
  }, ctx);
  // Rozmowa (makieta „Co powiesz?”, patch-50, 54 i 55): u góry głowa rozmówcy z jego kwestią po angielsku w dymku (imię, 🔊 i 🐢 — jak w scence) i dosłownym tłumaczeniem pod tekstem (pokazuje je 文A, jak
  // w scence), niżej brzoskwiniowe pole z poleceniem po polsku (pogrubione, zawsze widoczne: na poziomie 3 jest jedyną wskazówką) i podtytułem, potem wiersze z odpowiedziami (🔊 i 🐢) i „Dalej”
  // (bez maskotki i bez obrazka). Rozmówca to druga postać dialogu — ta, która nie mówi odpowiedzi (`task.partner`, krok `saySteps`).
  const partner = task.partner;
  const partnerPl = h('p', { class: 'pl', 'data-testid': 'partner-pl' }, partner.pl);
  const situationRow = dialogueLine(ctx, {
    who: partner.who,
    body: h('p', { class: 'say-partner sentence', 'data-testid': 'say-partner' }, partner.text),
    pl: partnerPl,
    testid: 'say-situation',
    play: { testid: 'partner-play', label: L.listenToSentence, run: () => { comp?.quiet(); return player.play(partner.audioId, { text: partner.text, lang: 'en' }); } },
    slow: { testid: 'partner-slow', label: L.slowly, run: () => { comp?.quiet(); return player.play(partner.slowAudioId, { text: partner.text, lang: 'en', slow: true }); } },
  });
  paintPartnerPl = () => { partnerPl.hidden = !showPl; };
  setPartnerBusy = (b) => situationRow.setBusy(b);
  // pole z poleceniem (makieta): pogrubione polecenie i pod nim podtytuł — oba po polsku; podtytuł zależy od trybu (dotykanie kart / mówienie) i od tego, czy poprawna odpowiedź jest pytaniem
  const answerText = task.accept[0] ?? correctCard?.text ?? '';
  const askSub = h('p', { class: 'say-ask-sub', 'data-testid': 'say-ask-sub' });
  const ask = h('div', { class: 'say-ask', 'data-testid': 'say-ask' }, h('p', { class: 'say-ask-title', 'data-testid': 'situation' }, task.situationPl), askSub);
  const paintAsk = (): void => {
    askSub.textContent = sayAskText(tapMode, answerText);
  };
  const root = h(
    'section',
    { class: 'task say exercise screen', 'data-task': 'say', 'data-state': 'idle', 'data-testid': 'task-say', ...sayFitProps(Math.max(...task.cards.map((c) => c.text.length), partner.text.length), task.situationPl.length) }, // o skali decydują karty, kwestia rozmówcy i polecenie (pogrubione, w polu nad kartami)
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), T.chip), plToggle, skipRow), // „Pomiń”: w prawym górnym rogu karty (poza układem)
    h('div', { class: 'ex-main screen-main' },
      h('div', { class: 'dlg' }, situationRow.el),
      ask,
      cardsBox,
    ),
    h('div', { class: 'result-box say-result-box' }, feedback), // wynik: tuż nad dolnym paskiem, na tablecie obok przycisku (styles.css, „wynik obok przycisku”)
    actions,
  );
  renderCards();
  renderControls();
  paintAsk();
  // zmiana języka uwag bota w trakcie zadania: komunikat od razu w nowym języku (bez ponownego czytania)
  const offLang = teacher?.onChange(() => {
    if (fb) feedback.replaceChildren(...fb());
  });
  const fit = fitToWindow(root, { grow: true });
  refit = fit.refit;
  return { el: root, destroy: () => { fit.dispose(); offLang?.(); comp?.taskBusy(false); player.stop(); } };
}
